const defaultTitle = 'Elker - Anonymous Reporting for Employees to Speak Up'
const defaultDescription = 'Elker is a speak-up and whistleblowing platform, empowering employees to make their voices heard, anonymously and safely.'
const defaultImage = 'https://media.elker.com/5cfeaa8c-2d96-4272-b445-14aa107c5311/speak-up-static.png'
const siteOrigin = 'https://elker.com'

// Paths (without /nz prefix) for NZ pages whose content is identical or near-identical to the AU equivalent.
// These pages declare the AU URL as canonical so Google consolidates indexation signal onto the AU page.
// hreflang continues to point to both locales so NZ visitors are still served the NZ URL.
//
// Add a path here when the NZ variant offers no NZ-distinct content. Remove a path when the NZ variant
// gets genuinely localised (NZ statutes, NZ examples, NZ regulator references) and should rank on its own.
const SHARED_NZ_PATHS = new Set([
  // Brand/chrome
  '/about',
  '/contact',
  '/trust',
  '/security',
  '/pricing',
  '/book-demo',
  // Universal product pages
  '/solutions/speak-up-platform',
  // Legal/policy (identical wording across locales)
  '/policies/whistleblowing',
  '/policies/cookies',
  '/policies/anti-modern-slavery',
  '/policies/terms',
  '/policies/privacy',
  // Thin blog category page
  '/blog/compliance',
  // Articles with no NZ-distinct content
  '/articles/anonymous-employee-feedback',
  '/articles/anonymous-reporting-advantages-disadvantages',
  '/articles/bystander-effect-in-the-workplace',
  '/articles/combating-virtual-harassment-in-remote-work',
  '/articles/culture-audit-guide',
  '/articles/cybersecurity-culture',
  '/articles/dealing-with-workplace-misconduct',
  '/articles/employee-engagement-survey-questions',
  '/articles/person-centred-and-trauma-informed-approach',
  '/articles/serious-misconduct',
  '/articles/shirli-kirschner-game-changers',
  '/articles/signs-of-a-toxic-workplace',
  '/articles/understanding-and-preventing-workplace-bullying',
  '/articles/whistleblowing-software',
  '/articles/what-is-whistleblowing',
  // Universal webinars (recordings, no NZ-specific framing)
  '/webinars/safety-culture-and-transformation-hospitality',
  '/webinars/2024-august-live-demonstration-of-the-elker-platform',
  '/webinars/addressing-psychosocial-issues-at-work-webinar',
  '/webinars/2024-sept-respect-at-work-webinar',
  '/webinars/dealing-with-sensitive-information',
  '/webinars/psychosocial-risks-leadership-and-tools',
])

function computeCanonical(url, locale) {
  if (locale === 'nz') {
    const auPath = url.replace(/^\/nz/, '') || '/'
    if (SHARED_NZ_PATHS.has(auPath)) {
      return `${siteOrigin}${auPath === '/' ? '' : auPath}`
    }
  }
  return `${siteOrigin}${url === '/' ? '' : url}`
}


function getImageUrl(imageObj) {
  if (!imageObj) return defaultImage

  if (typeof imageObj === 'string') return imageObj

  if (imageObj.id && imageObj.filename_download) {
    return `https://media.elker.com/${imageObj.id}/${imageObj.filename_download}`
  }

  return defaultImage
}


function normalisePath(url) {
  if (!url || url === '/') return ''
  return url.startsWith('/') ? url : `/${url}`
}


function detectLocale(path) {
  return path.startsWith('/nz') ? 'nz' : 'au'
}


function getLocaleAlternates(url) {
  const path = normalisePath(url)
  const nzStripped = path.replace(/^\/nz/, '')
  const auUrl = `${siteOrigin}${nzStripped || ''}` || siteOrigin
  const nzUrl = `${siteOrigin}/nz${nzStripped}`

  return {
    au: auUrl,
    nz: nzUrl
  }
}


function useSocialMeta(content, url, locale) {
  const { title, description, image } = content
  const imageUrl = getImageUrl(image)
  const fullUrl = `${siteOrigin}${url === '/' ? '' : url}`
  const ogLocale = locale === 'nz' ? 'en_NZ' : 'en_AU'
  const ogLocaleAlternate = locale === 'nz' ? 'en_AU' : 'en_NZ'

  return [
    {
      hid: 'og:title',
      property: 'og:title',
      content: title,
    },
    {
      hid: 'og:description',
      property: 'og:description',
      content: description,
    },
    {
      hid: 'og:image',
      property: 'og:image',
      content: imageUrl,
    },
    {
      hid: 'og:url',
      property: 'og:url',
      content: fullUrl,
    },
    {
      hid: 'og:site_name',
      property: 'og:site_name',
      content: 'Elker',
    },
    {
      hid: 'og:type',
      property: 'og:type',
      content: 'website',
    },
    {
      hid: 'og:locale',
      property: 'og:locale',
      content: ogLocale,
    },
    {
      hid: 'og:locale:alternate',
      property: 'og:locale:alternate',
      content: ogLocaleAlternate,
    },

    // Twitter meta
    {
      hid: 'twitter:card',
      name: 'twitter:card',
      content: 'summary_large_image',
    },
    {
      hid: 'twitter:title',
      name: 'twitter:title',
      content: title,
    },
    {
      hid: 'twitter:description',
      name: 'twitter:description',
      content: description,
    },
    {
      hid: 'twitter:image',
      name: 'twitter:image',
      content: imageUrl,
    },
    {
      hid: 'twitter:site',
      name: 'twitter:site',
      content: '@ElkerApp',
    },
  ]
}

export const useSeo = (meta, url, options = {}) => {
  const locale = detectLocale(url || '/')
  const alternates = options.alternates === false ? null : getLocaleAlternates(url || '/')
  const robotsMeta = options.noindex
    ? [{ hid: 'robots', name: 'robots', content: 'noindex,nofollow' }]
    : []

  const buildLinks = (canonical) => {
    const feedHref = locale === 'nz' ? `${siteOrigin}/nz/feed.xml` : `${siteOrigin}/feed.xml`
    const feedTitle = locale === 'nz' ? 'Elker New Zealand articles' : 'Elker Australia articles'
    const links = [
      {
        hid: 'canonical',
        rel: 'canonical',
        href: canonical,
      },
      {
        hid: 'feed',
        rel: 'alternate',
        type: 'application/rss+xml',
        title: feedTitle,
        href: feedHref,
      },
    ]
    if (alternates) {
      links.push(
        {
          hid: 'i18n-alt-en-au',
          rel: 'alternate',
          hreflang: 'en-AU',
          href: alternates.au
        },
        {
          hid: 'i18n-alt-en-nz',
          rel: 'alternate',
          hreflang: 'en-NZ',
          href: alternates.nz
        },
        {
          hid: 'i18n-alt-default',
          rel: 'alternate',
          hreflang: 'x-default',
          href: alternates.au
        }
      )
    }
    return links
  }

  if (!meta) {
    const fallbackCanonical = options.forceCanonical || computeCanonical(url || '/', locale)
    return {
      title: defaultTitle,
      link: buildLinks(fallbackCanonical),
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: defaultDescription,
        },
        ...useSocialMeta({
          title: defaultTitle,
          description: defaultDescription,
          image: null
        }, url, locale),
        ...robotsMeta,
      ],
    }
  }

  const { meta_title, meta_description, meta_image } = meta

  const title = meta_title || defaultTitle
  const description = meta_description || defaultDescription

  const socialMetas = useSocialMeta({
    title,
    description,
    image: meta_image
  }, url, locale)

  const canonicalHref = options.forceCanonical || computeCanonical(url, locale)

  return {
    title,
    link: buildLinks(canonicalHref),
    meta: [
      {
        hid: 'description',
        name: 'description',
        content: description,
      },
      ...socialMetas,
      ...robotsMeta,
    ],
  }
}
